import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@utils/cn';

interface StarToggleProps {
  active: boolean;
  onToggle: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const sizeClasses = {
  sm: 'w-7 h-7',
  md: 'w-8 h-8',
};

const iconSize = {
  sm: 14,
  md: 16,
};

// Favorite star toggle (atom). Filled amber when active, outline slate otherwise.
// Stops propagation so toggling does not trigger a parent card's navigation.
export const StarToggle: React.FC<StarToggleProps> = ({
  active,
  onToggle,
  disabled = false,
  size = 'sm',
  className = '',
}) => {
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!disabled) onToggle();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={active}
      aria-label={active ? 'Remove from favorites' : 'Add to favorites'}
      title={active ? 'Remove from favorites' : 'Add to favorites'}
      className={cn(
        'inline-flex items-center justify-center rounded-full border transition-colors flex-shrink-0',
        sizeClasses[size],
        active
          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
          : 'border-gray-200 dark:border-gray-600 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-600',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <Star
        size={iconSize[size]}
        className={active ? 'text-amber-400 fill-amber-400' : 'text-current'}
      />
    </button>
  );
};

export default StarToggle;
